/* EnquiryModal.tsx */
import React, { useRef, useState } from "react";
import closeIcon from "../assets/icon simple close.png";
import { motion, AnimatePresence } from "framer-motion";
import SubmitJSON from "submitjson";                // ① yarn add submitjson  |  npm i submitjson

interface EnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

/* ----------------------------------------
   0.  Configure your SubmitJSON instance
       – keep keys in .env
   ---------------------------------------- */
const sj = new SubmitJSON({
  apiKey: 'sjk_c4380b32694d41f8a3762dd1c3461a87',          // sjk_xxxxxx  (omit if “API key not required”)
  endpoint: 'ZeF9F3Spb',       // slug from the dashboard, e.g. "abc123"
});

const countryCodes = [
  { code: "+91", label: "India" },
  { code: "+1", label: "USA" },
  { code: "+44", label: "UK" },
];

const EnquiryModal: React.FC<EnquiryModalProps> = ({ open, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  /* ----------------------------------------
     1. Controlled inputs (keeps code concise)
     ---------------------------------------- */
  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    message: "",
    offers: false,
  });
  const [sending, setSending] = useState(false);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) onClose();
  };

  /* ----------------------------------------
     2. Submit handler → SubmitJSON
     ---------------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    try {
      await sj.submit({
        name: form.name,
        email: form.email,
        phone: `${form.countryCode} ${form.phone}`,
        message: form.message,
        wantsOffers: form.offers,
      });
      alert("Thanks! We’ll be in touch shortly.");
      setForm({ name: "", email: "", countryCode: "+91", phone: "", message: "", offers: false });
      onClose();                                    // close the modal if you like
    } catch (err: any) {
      console.error(err);
      alert("Sorry — something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div
          ref={modalRef}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-[100] flex justify-end bg-black/30"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="relative w-full max-w-[612px] h-[845px] bg-[#f5f7fa] p-8 flex flex-col"
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-6 p-2"
              aria-label="Close"
            >
              <img src={closeIcon} alt="Close" className="w-7 h-7" />
            </button>

            {/* Title */}
            <h2 className="font-utility text-[24px] font-medium text-[#011621] mb-6 mt-[46px] leading-tight mx-[88px]">
              Complete The Form Below And<br />Our Representatives Will<br />Contact You Shortly!
            </h2>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 flex-1 w-[372px] mx-[88px]"
            >
              {/* Full name */}
              <div>
                <label className="block text-[16px] text-[#033652] mb-1">Full Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Enter your name"
                  className="w-full px-4 py-2 bg-white focus:ring-2 focus:ring-[#1e5d66]"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[16px] text-[#033652] mb-1">Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-2 bg-white focus:ring-2 focus:ring-[#1e5d66]"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[16px] text-[#033652] mb-1">Phone Number</label>
                <div className="flex">
                  <select
                    value={form.countryCode}
                    onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                    className="px-2 py-2 bg-white text-[#4F7286] focus:ring-2 focus:ring-[#1e5d66]"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone number"
                    className="w-full px-4 py-2 bg-white focus:ring-2 focus:ring-[#1e5d66]"
                    required
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[16px] text-[#033652] mb-1">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Please type your message here"
                  className="w-full px-4 py-2 bg-white min-h-[80px] focus:ring-2 focus:ring-[#1e5d66]"
                  required
                />
              </div>

              {/* Offers opt-in */}
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="offers"
                  checked={form.offers}
                  onChange={(e) => setForm({ ...form, offers: e.target.checked })}
                  className="mr-2 h-[16px] w-[16px]"
                />
                <label htmlFor="offers" className="text-[14px] text-[#011621]">
                  I wish to receive offers & promotions
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-[#07394a] text-white py-2 text-[18px] font-medium mt-4 hover:bg-[#46C1BF] transition-all duration-100
                           [clip-path:polygon(0_0,100%_0,100%_100%,0_100%)]
                           hover:[clip-path:polygon(0_0,100%_0,100%_calc(100%_-_16px),calc(100%_-_16px)_100%,0_100%)]"
              >
                {sending ? "Sending…" : "Submit"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryModal;
